import { useEffect, useMemo, useState } from "react";
import { canAccess, userRoles } from "@/lib/constants";
import client, { apiError, downloadCsv } from "@/api/client";
import { PageHeader, Section, StatusPill } from "@/components/PageChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useUserNames } from "@/hooks/useUserNames";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, DownloadSimple, MagnifyingGlass, XCircle, FunnelSimple, ArrowUUpLeft } from "@phosphor-icons/react";

const EMPTY = {
  company_id: "", order_no: "", retailer_id: "", salesman: "",
  order_quantity: "", order_value: "", hold_by: "", remarks: "",
};

export default function Orders() {
  const { user } = useAuth();
  const isAdmin = userRoles(user).includes("admin");
  const canCreate = canAccess(user, ["accounts"]);
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [retailerSearch, setRetailerSearch] = useState("");
  const [retailerSearchOpen, setRetailerSearchOpen] = useState(false);
  const { nameFor } = useUserNames();

  // Filters
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo,   setFilterDateTo]   = useState("");
  const [filterSalesman, setFilterSalesman] = useState("all");
  const [filterRetailerName, setFilterRetailerName] = useState("");
  const [filterRetailerCode, setFilterRetailerCode] = useState("");

  const load = async () => {
    const [{ data }, { data: c }, { data: r }, { data: s }] = await Promise.all([
      client.get("/orders"), client.get("/companies"), client.get("/retailers"), client.get("/salesmen"),
    ]);
    setRows(data); setCompanies(c); setRetailers(r); setSalesmen(s);
  };
  useEffect(() => { load(); }, []);

  const selectedRetailer = useMemo(
    () => retailers.find(r => r.id === form.retailer_id),
    [retailers, form.retailer_id]
  );

  const retailerMatches = useMemo(() => {
    const s = retailerSearch.trim().toLowerCase();
    if (!s) return retailers.slice(0, 20);
    return retailers.filter(r =>
      (r.name || "").toLowerCase().includes(s) ||
      (r.retailer_code || "").toLowerCase().includes(s)
    ).slice(0, 20);
  }, [retailers, retailerSearch]);

  const filteredRows = useMemo(() => {
    const rname = filterRetailerName.trim().toLowerCase();
    const rcode = filterRetailerCode.trim().toLowerCase();
    return rows.filter(r => {
      if (filterDateFrom || filterDateTo) {
        const dt = r.order_datetime ? new Date(r.order_datetime) : null;
        if (!dt) return false;
        if (filterDateFrom && dt < new Date(filterDateFrom + "T00:00:00")) return false;
        if (filterDateTo && dt > new Date(filterDateTo + "T23:59:59")) return false;
      }
      if (filterSalesman && filterSalesman !== "all" && r.salesman !== filterSalesman) return false;
      if (rname && !(r.retailer_name || "").toLowerCase().includes(rname)) return false;
      if (rcode) {
        const ret = retailers.find(x => x.id === r.retailer_id);
        const code = (ret?.retailer_code || "").toLowerCase();
        if (!code.includes(rcode)) return false;
      }
      return true;
    });
  }, [rows, filterDateFrom, filterDateTo, filterSalesman, filterRetailerName, filterRetailerCode, retailers]);

  const clearFilters = () => {
    setFilterDateFrom(""); setFilterDateTo("");
    setFilterSalesman("all"); setFilterRetailerName(""); setFilterRetailerCode("");
  };

  const hasFilters = filterDateFrom || filterDateTo ||
    (filterSalesman && filterSalesman !== "all") ||
    filterRetailerName || filterRetailerCode;

  const openNew = () => {
    setForm(EMPTY); setEditId(null);
    setRetailerSearch(""); setRetailerSearchOpen(false);
    setOpen(true);
  };
  const openEdit = (r) => {
    setForm({
      company_id: r.company_id, order_no: r.order_no, retailer_id: r.retailer_id,
      salesman: r.salesman, order_quantity: r.order_quantity,
      order_value: r.order_value ?? "", hold_by: r.hold_by || "", remarks: r.remarks || "",
    });
    const ret = retailers.find(x => x.id === r.retailer_id);
    setRetailerSearch(ret ? `${ret.name}${ret.retailer_code ? ` · ${ret.retailer_code}` : ""}` : "");
    setRetailerSearchOpen(false);
    setEditId(r.id); setOpen(true);
  };

  const pickRetailer = (r) => {
    setForm(prev => ({ ...prev, retailer_id: r.id, salesman: prev.salesman || r.salesman || "" }));
    setRetailerSearch(`${r.name}${r.retailer_code ? ` · ${r.retailer_code}` : ""}`);
    setRetailerSearchOpen(false);
  };

  const clearRetailer = () => {
    setForm(prev => ({ ...prev, retailer_id: "" }));
    setRetailerSearch("");
    setRetailerSearchOpen(false);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        order_quantity: Number(form.order_quantity),
        order_value: Number(form.order_value || 0),
      };
      if (editId) await client.put(`/orders/${editId}`, payload); else await client.post("/orders", payload);
      toast.success(editId ? "Updated" : "Order created"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this order?")) return;
    try { await client.delete(`/orders/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const revert = async (row) => {
    if (row.stage === "ordered") return toast.info("Already at Ordered stage");
    if (!confirm(`Revert ${row.order_no} to Ordered?\n\nThis clears picking, scanning, packing and dispatch data for this order.`)) return;
    try {
      const { data } = await client.post(`/orders/${row.id}/revert-to-ordered`);
      toast.success(`Reverted ${row.order_no} (was ${data.previous_stage})`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Orders" subtitle="All orders across stages"
        actions={<div className="flex gap-2">
          <Button variant="outline" className="rounded-sm" data-testid="orders-export-btn" onClick={()=>downloadCsv("/export/orders", "orders.csv")}>
            <DownloadSimple size={16} className="mr-1"/> CSV
          </Button>
          {canCreate && <Button data-testid="order-new-btn" onClick={openNew} className="bg-slate-900 hover:bg-slate-800 rounded-sm"><Plus size={16} weight="bold" className="mr-1"/> New Order</Button>}
        </div>}
      />
      <Section>
        {/* Filters */}
        <div data-testid="orders-filter-bar" className="bg-white border border-slate-200 rounded-sm p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <FunnelSimple size={16} className="text-slate-600" weight="duotone"/>
            <div className="label-cap text-slate-700">Filters</div>
            {hasFilters && (
              <button
                data-testid="orders-filter-clear"
                onClick={clearFilters}
                className="ml-auto text-xs font-semibold text-rose-600 hover:underline flex items-center gap-1"
              >
                <XCircle size={13}/> Clear all
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <label className="label-cap">Date from</label>
              <Input data-testid="orders-filter-date-from" type="date" value={filterDateFrom} onChange={(e)=>setFilterDateFrom(e.target.value)}/>
            </div>
            <div>
              <label className="label-cap">Date to</label>
              <Input data-testid="orders-filter-date-to" type="date" value={filterDateTo} onChange={(e)=>setFilterDateTo(e.target.value)}/>
            </div>
            <div>
              <label className="label-cap">Salesman</label>
              <Select value={filterSalesman} onValueChange={setFilterSalesman}>
                <SelectTrigger data-testid="orders-filter-salesman" className="rounded-sm"><SelectValue placeholder="All"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {salesmen.map(s => <SelectItem key={s.mobile} value={s.name}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-cap">Retailer name</label>
              <Input data-testid="orders-filter-retailer-name" placeholder="Contains…" value={filterRetailerName} onChange={(e)=>setFilterRetailerName(e.target.value)}/>
            </div>
            <div>
              <label className="label-cap">Retailer code</label>
              <Input data-testid="orders-filter-retailer-code" placeholder="Contains…" value={filterRetailerCode} onChange={(e)=>setFilterRetailerCode(e.target.value)}/>
            </div>
          </div>
          <div className="mt-3 text-xs text-slate-500" data-testid="orders-filter-count">
            Showing <b>{filteredRows.length}</b> of {rows.length} orders
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left label-cap border-b border-slate-200 bg-slate-50">
              <th className="px-4 py-3">Order No</th>
              <th className="px-4 py-3">Date/Time</th>
              <th className="px-4 py-3">Company</th>
              <th className="px-4 py-3">Retailer</th>
              <th className="px-4 py-3">Salesman</th>
              <th className="px-4 py-3">Order Qty</th>
              <th className="px-4 py-3">Order Value</th>
              <th className="px-4 py-3">Hold By</th>
              <th className="px-4 py-3">Stage</th>
              <th className="px-4 py-3">Remarks</th>
              <th className="px-4 py-3">Entered By</th>
              {isAdmin && <th className="px-4 py-3 w-40">Actions</th>}
            </tr></thead>
            <tbody data-testid="orders-tbody">
              {filteredRows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 font-mono font-semibold">{r.order_no}</td>
                  <td className="px-4 py-2 font-mono text-xs text-slate-600">{new Date(r.order_datetime).toLocaleString()}</td>
                  <td className="px-4 py-2">{r.company_name}</td>
                  <td className="px-4 py-2">{r.retailer_name}</td>
                  <td className="px-4 py-2">{r.salesman}</td>
                  <td className="px-4 py-2 font-mono">{r.order_quantity}</td>
                  <td className="px-4 py-2 font-mono">{r.order_value ? Number(r.order_value).toFixed(2) : "—"}</td>
                  <td className="px-4 py-2 text-xs">{r.hold_by ? nameFor(r.hold_by) : "—"}</td>
                  <td className="px-4 py-2"><StatusPill status={r.stage} /></td>
                  <td className="px-4 py-2 text-xs text-slate-600 max-w-xs">{r.remarks || "—"}</td>
                  <td className="px-4 py-2 text-xs">{nameFor(r.entered_by)}</td>
                  {isAdmin && (
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap items-center gap-1">
                        <button data-testid={`order-edit-${r.id}`} onClick={() => openEdit(r)} className="p-1.5 hover:bg-slate-100 rounded-sm" title="Edit"><PencilSimple size={14}/></button>
                        <button data-testid={`order-delete-${r.id}`} onClick={() => remove(r.id)} className="p-1.5 hover:bg-rose-50 text-rose-600 rounded-sm" title="Delete"><Trash size={14}/></button>
                        <button
                          data-testid={`order-revert-${r.id}`}
                          onClick={() => revert(r)}
                          disabled={r.stage === "ordered"}
                          className="ml-1 flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-sm border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-40 disabled:cursor-not-allowed"
                          title={r.stage === "ordered" ? "Already at Ordered" : "Revert to Ordered — clears picking/scanning/packing/dispatch"}
                        >
                          <ArrowUUpLeft size={12} weight="bold"/> Revert
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {filteredRows.length === 0 && (
                <tr><td colSpan={isAdmin ? 12 : 11} className="px-4 py-10 text-center text-slate-500">
                  {rows.length === 0 ? "No orders" : "No orders match your filters"}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "New"} Order</DialogTitle></DialogHeader>
          <div className="grid gap-3 max-h-[70vh] overflow-y-auto pr-2">
            <div>
              <label className="label-cap">Company</label>
              <Select value={form.company_id} onValueChange={(v)=>setForm({...form, company_id: v})}>
                <SelectTrigger data-testid="order-company-select" className="rounded-sm"><SelectValue placeholder="Select company"/></SelectTrigger>
                <SelectContent>{companies.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-cap">Order No (unique)</label>
              <Input data-testid="order-no-input" value={form.order_no} onChange={(e)=>setForm({...form, order_no: e.target.value})} disabled={!!editId && !isAdmin}/>
            </div>
            <div className="relative">
              <label className="label-cap">Retailer (search by name or code)</label>
              <div className="relative">
                <Input
                  data-testid="order-retailer-search"
                  value={retailerSearch}
                  onChange={(e)=>{
                    setRetailerSearch(e.target.value);
                    setRetailerSearchOpen(true);
                    // If user is typing but selected retailer's display doesn't match, clear selection
                    if (selectedRetailer) {
                      const disp = `${selectedRetailer.name}${selectedRetailer.retailer_code ? ` · ${selectedRetailer.retailer_code}` : ""}`;
                      if (e.target.value !== disp) setForm(prev => ({...prev, retailer_id: ""}));
                    }
                  }}
                  onFocus={()=>setRetailerSearchOpen(true)}
                  placeholder="Type retailer name or code…"
                  className="pr-8"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  {form.retailer_id ? (
                    <button
                      type="button"
                      onClick={clearRetailer}
                      data-testid="order-retailer-clear"
                      className="text-slate-400 hover:text-rose-600"
                    ><XCircle size={16}/></button>
                  ) : (
                    <MagnifyingGlass size={14} className="text-slate-400"/>
                  )}
                </div>
              </div>
              {retailerSearchOpen && (
                <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-60 overflow-y-auto z-50" data-testid="order-retailer-suggestions">
                  {retailerMatches.length === 0 && (
                    <div className="px-3 py-2 text-xs text-slate-500">No retailers match “{retailerSearch}”</div>
                  )}
                  {retailerMatches.map(r => (
                    <button
                      key={r.id}
                      type="button"
                      data-testid={`order-retailer-suggestion-${r.id}`}
                      onClick={() => pickRetailer(r)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-orange-50 flex items-center justify-between gap-3 border-b border-slate-100 last:border-b-0"
                    >
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{r.name}</div>
                        <div className="text-xs text-slate-500 truncate">{r.city || "—"}{r.area ? `, ${r.area}` : ""}</div>
                      </div>
                      <div className="text-xs font-mono text-slate-700 shrink-0">{r.retailer_code || "—"}</div>
                    </button>
                  ))}
                </div>
              )}
              {retailerSearchOpen && (
                <div
                  className="fixed inset-0 z-40"
                  onClick={()=>setRetailerSearchOpen(false)}
                  aria-hidden="true"
                />
              )}
            </div>
            <div>
              <label className="label-cap">Salesman</label>
              <Select value={form.salesman} onValueChange={(v)=>setForm({...form, salesman: v})}>
                <SelectTrigger data-testid="order-salesman-select" className="rounded-sm"><SelectValue placeholder="Select salesman"/></SelectTrigger>
                <SelectContent>{salesmen.map(s=><SelectItem key={s.mobile} value={s.name}>{s.name} · {s.mobile}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-cap">Order Quantity</label><Input data-testid="order-quantity-input" type="number" value={form.order_quantity} onChange={(e)=>setForm({...form, order_quantity: e.target.value})}/></div>
              <div><label className="label-cap">Order Value</label><Input data-testid="order-value-input" type="number" step="0.01" value={form.order_value} onChange={(e)=>setForm({...form, order_value: e.target.value})}/></div>
            </div>
            <div>
              <label className="label-cap">Hold By (Salesman)</label>
              <Select value={form.hold_by} onValueChange={(v)=>setForm({...form, hold_by: v})}>
                <SelectTrigger data-testid="order-holdby-select" className="rounded-sm"><SelectValue placeholder="— none —"/></SelectTrigger>
                <SelectContent>{salesmen.map(s=><SelectItem key={s.mobile} value={s.mobile}>{s.name} · {s.mobile}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-cap">Remarks</label>
              <Textarea data-testid="order-remarks-input" value={form.remarks} onChange={(e)=>setForm({...form, remarks: e.target.value})} rows={2}/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="order-save-btn" onClick={save} className="bg-slate-900 hover:bg-slate-800 rounded-sm">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
